/**
 * Modal de Picking (Separação) via SIAL Web — Opção 2 (sistema).
 *
 * Com controle de caixas: operador pode registrar itens na caixa atual, lacrar,
 * abrir outra caixa e continuar no mesmo modal (persistência incremental).
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';
import {
	escHtml,
	sumQtyByLine,
	openCreateBoxModal,
	STATUS_BOX_LABEL,
	STATUS_BOX_STYLE,
} from '../utils/output-picking-box-ops.js';

/** Rótulos de status da saída. */
const STATUS_LABEL = {
	10: 'Criada',
	20: 'Separando',
	30: 'Separada',
	40: 'Conferindo',
	50: 'Unpick',
	60: 'Conferida',
	65: 'Faturando',
	70: 'Faturada',
	80: 'Expedição',
	90: 'Encerrada',
	100: 'Cancelada',
};

/** Status da saída que permitem separação web. */
const ALLOWED_STATUS = new Set([10, 20, 30, 40, 50]);

/**
 * Abre o modal de Picking (Separação) Web.
 *
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {function} [onSuccess] - chamado após persistência bem-sucedida
 */
export default async function openOutputPickingModal(companyId, outputId, onSuccess) {

	let output, companyRaw, boxes;
	try {
		[output, companyRaw] = await Promise.all([
			api.call(api.select_output, companyId, outputId),
			api.call(api.select_company, companyId),
		]);
	} catch (e) {
		notify(e?.message || 'Erro ao carregar dados da saída.', 'Picking');
		return;
	}

	if (!output) {
		notify(`Saída ${outputId} não encontrada.`, 'Picking');
		return;
	}

	if (!ALLOWED_STATUS.has(output.status)) {
		notify(
			output.status >= 60
				? `Saída ${outputId} já conferida (status ${output.status} — ${STATUS_LABEL[output.status] || output.status}). Separação não permitida.`
				: `Status ${output.status} (${STATUS_LABEL[output.status] || '?'}) não permite separação web.`,
			'Picking'
		);
		return;
	}

	const enableBoxCtrl = companyRaw?.out_enable_box_control === true;

	if (enableBoxCtrl) {
		try {
			const boxData = await api.call(api.list_output_picking_boxes, companyId, outputId);
			boxes = boxData?.boxes || [];
		} catch (_) {
			boxes = [];
		}
	}

	let items = output.items || [];
	let selectedBoxId = null;

	function openBoxes() {
		return (boxes || []).filter(b => Number(b.status) === 10);
	}

	function getSelectedBoxId() {
		return selectedBoxId;
	}

	function getSelectedBox() {
		const id = getSelectedBoxId();
		if (!id) return null;
		return (boxes || []).find(b => String(b.id) === String(id)) || null;
	}

	async function reloadBoxes() {
		const boxData = await api.call(api.list_output_picking_boxes, companyId, outputId);
		boxes = boxData?.boxes || [];
	}

	function buildRowsHtml() {
		if (!items.length) {
			return `<tr><td colspan="8" style="padding:1rem;text-align:center;color:#888">Esta saída não possui itens.</td></tr>`;
		}

		return items.map(item => {
			const num = Number(item.number);
			const sku = item.product_sku ? String(item.product_sku).trim() : '';
			const name = item.product_name ? String(item.product_name).trim() : `Produto #${item.product_id}`;
			const lot = item.lot_number ? String(item.lot_number).trim() : '—';
			const location = item.location_code ? String(item.location_code).trim() : '—';
			const required = Number(item.quantity_required) || 0;
			const supplied = Number(item.quantity_supplied) || 0;
			const pending = Math.max(0, required - supplied);
			const isTertiary = !!item.is_tertiary_volume;
			const tertiaryBadge = isTertiary
				? `<span style="display:inline-block;font-size:0.7rem;background:#ddf;color:#449;border-radius:2px;padding:1px 4px;margin-left:4px">sem caixa</span>`
				: '';
			const boxHint = (enableBoxCtrl && isTertiary)
				? `<div style="font-size:0.7rem;color:#888;margin-top:2px">Não entra em caixa — informe em Separar agora e clique Salvar.</div>`
				: '';
			const inputDisabled = pending <= 0;

			return `
				<tr data-num="${num}" style="${inputDisabled ? 'opacity:0.6' : ''}">
					<td style="padding:5px 7px;color:#888;font-size:0.82rem;white-space:nowrap;vertical-align:middle">${num}</td>
					<td style="padding:5px 7px;font-size:0.8rem;white-space:nowrap;vertical-align:middle">${escHtml(location)}</td>
					<td style="padding:5px 7px;min-width:160px;vertical-align:middle">
						<div style="font-size:0.7rem;color:#666">${escHtml(sku)}</div>
						<div style="font-size:0.84rem">${escHtml(name)}${tertiaryBadge}</div>
						${boxHint}
					</td>
					<td style="padding:5px 7px;white-space:nowrap;font-size:0.82rem;vertical-align:middle">${escHtml(lot)}</td>
					<td style="padding:5px 7px;text-align:right;font-weight:500;vertical-align:middle">${required}</td>
					<td style="padding:5px 7px;text-align:right;color:#555;vertical-align:middle" class="pick-col-supplied">${supplied}</td>
					<td style="padding:5px 7px;text-align:right;color:${pending > 0 ? '#c80' : '#2a7'};font-weight:500;vertical-align:middle" class="pick-col-pending">${pending}</td>
					<td style="padding:5px 7px;vertical-align:middle">
						<input type="number"
							class="pick-qty-input"
							data-num="${num}"
							data-pending="${pending}"
							data-supplied="${supplied}"
							data-required="${required}"
							data-is-tertiary="${isTertiary ? '1' : '0'}"
							min="0" max="${pending}" step="1" value="0"
							${inputDisabled ? 'disabled' : ''}
							style="width:5rem;text-align:right;padding:2px 5px;${inputDisabled ? 'background:#f5f5f5' : ''}">
					</td>
				</tr>`;
		}).join('');
	}

	function buildBoxPanelHtml() {
		if (!enableBoxCtrl) return '';

		const openList = openBoxes();
		const boxOpts = openList.map(b =>
			`<option value="${escHtml(b.id)}"${String(b.id) === String(selectedBoxId) ? ' selected' : ''}>${escHtml(b.code)}</option>`
		).join('');

		if (!selectedBoxId && openList.length === 1) {
			selectedBoxId = String(openList[0].id);
		}

		const summaryRows = (boxes || []).length
			? (boxes || []).map(box => {
				const st = Number(box.status);
				const label = STATUS_BOX_LABEL[st] || st;
				const style = STATUS_BOX_STYLE[st] || '';
				const itemCount = box.items?.length || 0;
				const qtyTotal = box.items?.reduce?.((s, i) => s + (Number(i.quantity) || 0), 0) || 0;
				return `<tr>
					<td style="padding:3px 6px">${escHtml(box.code)}</td>
					<td style="padding:3px 6px"><span style="display:inline-block;padding:1px 6px;border-radius:3px;font-size:0.78em;${style}">${label}</span></td>
					<td style="padding:3px 6px;text-align:right">${itemCount}</td>
					<td style="padding:3px 6px;text-align:right">${qtyTotal}</td>
				</tr>`;
			}).join('')
			: `<tr><td colspan="4" style="padding:6px;color:#888;font-style:italic">Nenhuma caixa nesta saída.</td></tr>`;

		const selId = selectedBoxId || '';

		return `
			<div id="pick-box-panel" style="margin-top:1rem;padding:0.75rem;background:#f0f4ff;border:1px solid #c0cff0;border-radius:4px">
				<div style="font-weight:600;font-size:0.88rem;margin-bottom:0.5rem">Caixas de Picking</div>
				<p style="margin:0 0 0.5rem;font-size:0.78rem;color:#668">
					Fluxo: informe as quantidades → <strong>Registrar nesta caixa</strong> → <strong>Lacrar</strong> quando encher → <strong>Abrir nova caixa</strong> e continue.
					Itens marcados <strong>sem caixa</strong> usam <strong>Separar agora</strong> e o botão <strong>Salvar</strong>.
					Quando tudo já estiver registrado nas caixas, clique <strong>Salvar</strong> para concluir.
				</p>
				<div style="display:flex;align-items:flex-end;gap:0.5rem;flex-wrap:wrap;margin-bottom:0.5rem">
					<label style="font-size:0.87rem">
						Caixa ativa:
						<select id="pick-box-select" style="margin-left:4px;padding:3px 6px;min-width:8rem">
							<option value="">— selecione —</option>
							${boxOpts}
						</select>
					</label>
					<label style="font-size:0.87rem">
						Nº lacre (opcional):
						<input id="pick-box-seal-input" type="text" maxlength="40"
							style="display:block;margin-top:2px;padding:3px 6px;min-width:9rem;box-sizing:border-box"
							placeholder="Lacre da caixa" autocomplete="off">
					</label>
					<button type="button" id="pick-box-register-btn" class="button" style="padding:4px 10px;font-size:0.82rem">Registrar nesta caixa</button>
					<button type="button" id="pick-box-seal-btn" class="button" style="padding:4px 10px;font-size:0.82rem">Lacrar caixa atual</button>
					<button type="button" id="pick-box-new-btn" class="button" style="padding:4px 10px;font-size:0.82rem">Abrir nova caixa</button>
				</div>
				<div style="overflow-x:auto;max-height:120px;overflow-y:auto">
					<table style="width:100%;border-collapse:collapse;font-size:0.8em">
						<thead>
							<tr style="background:#e8eeff">
								<th style="padding:3px 6px;text-align:left">Caixa</th>
								<th style="padding:3px 6px;text-align:left">Status</th>
								<th style="padding:3px 6px;text-align:right">Itens</th>
								<th style="padding:3px 6px;text-align:right">Qtd.</th>
							</tr>
						</thead>
						<tbody id="pick-box-summary-body">${summaryRows}</tbody>
					</table>
				</div>
			</div>`;
	}

	function refreshTable(node) {
		const tbody = node.querySelector('#pick-items-table tbody');
		if (tbody) tbody.innerHTML = buildRowsHtml();
	}

	function refreshBoxPanel(node) {
		const panel = node.querySelector('#pick-box-panel');
		if (!panel || !enableBoxCtrl) return;
		const tmp = document.createElement('div');
		tmp.innerHTML = buildBoxPanelHtml();
		const newPanel = tmp.firstElementChild;
		if (newPanel) {
			panel.replaceWith(newPanel);
			bindBoxPanelEvents(node);
		}
	}

	function collectEntries(node) {
		const inputs = Array.from(node.querySelectorAll('.pick-qty-input:not([disabled])'));
		const entries = [];

		for (const inp of inputs) {
			const raw = inp.value.trim();
			if (!raw || raw === '0') continue;
			const qty = parseInt(raw, 10);
			if (isNaN(qty) || qty <= 0) continue;

			const num = parseInt(inp.dataset.num, 10);
			const pending = parseInt(inp.dataset.pending, 10);
			if (qty > pending) {
				return { error: `Linha ${num}: quantidade ${qty} ultrapassa o pendente (${pending}).`, inp };
			}

			const supplied = parseInt(inp.dataset.supplied, 10);
			const required = parseInt(inp.dataset.required, 10);
			entries.push({
				num,
				qty,
				newSupplied: supplied + qty,
				required,
				isTertiary: inp.dataset.isTertiary === '1',
			});
		}
		return { entries };
	}

	function applySuppliedLocally(entries) {
		for (const e of entries) {
			const item = items.find(i => Number(i.number) === e.num);
			if (item) item.quantity_supplied = e.newSupplied;
		}
		if (output.status === 10) output.status = 20;
	}

	function clearPickInputs(node) {
		for (const inp of node.querySelectorAll('.pick-qty-input:not([disabled])')) {
			inp.value = '0';
		}
	}

	function getPickBoxSealCode(node) {
		return node?.querySelector('#pick-box-seal-input')?.value?.trim() || null;
	}

	function clearPickBoxSealInput(node) {
		const sealInp = node?.querySelector('#pick-box-seal-input');
		if (sealInp) sealInp.value = '';
	}

	async function sealCurrentBox(node, boxId) {
		const box = getSelectedBox();
		const itemCount = box?.items?.length || 0;
		if (itemCount === 0) {
			notify('A caixa não possui itens — não é possível lacrar.', 'Lacrar Caixa');
			return false;
		}
		const sealRes = await api.call(
			api.close_output_picking_box,
			companyId, outputId, boxId,
			{ seal_code: getPickBoxSealCode(node) }
		);
		if (sealRes?.error) {
			notify(sealRes.error, 'Erro ao lacrar');
			return false;
		}
		selectedBoxId = null;
		clearPickBoxSealInput(node);
		notify(`Caixa "${escHtml(box?.code || '')}" lacrada. Pode abrir outra caixa e continuar.`, 'Caixa Lacrada');
		return true;
	}

	async function patchOutputSupplied(entries) {
		const itemsUpdated = items.map(item => {
			const entry = entries.find(e => e.num === Number(item.number));
			return {
				number: item.number,
				location_id: item.location_id,
				warehouse_id: item.warehouse_id,
				product_id: item.product_id,
				lot_id: item.lot_id,
				quantity_required: item.quantity_required,
				quantity_supplied: entry ? entry.newSupplied : (item.quantity_supplied ?? null),
				discount: item.discount ?? 0,
				unit_cost: 0,
				unit_price: 0,
			};
		});

		const patchPayload = {
			order_type: output.order_type,
			order_id: output.order_id,
			warehouse_company_id: output.warehouse_company_id,
			warehouse_id: output.warehouse_id,
			volume_type: output.volume_type,
			volumes: output.volumes,
			weight: output.weight,
			status: output.status === 10 ? 20 : output.status,
			items: itemsUpdated,
		};

		const patchRes = await api.call(api.patch_output, companyId, outputId, patchPayload);
		if (patchRes?.error) throw new Error(patchRes.error);
	}

	function validateBoxCaps(boxId, nonTertiaryEntries) {
		const box = (boxes || []).find(b => String(b.id) === String(boxId));
		const qtyInBox = {};
		for (const bi of (box?.items || [])) {
			qtyInBox[Number(bi.output_item_number)] = Number(bi.quantity) || 0;
		}
		const sums = sumQtyByLine(boxes || [], boxId);

		for (const e of nonTertiaryEntries) {
			const newInBox = (qtyInBox[e.num] || 0) + e.qty;
			const others = sums[e.num] || 0;
			if (others + newInBox > e.required) {
				const avail = e.required - others - (qtyInBox[e.num] || 0);
				return `Linha ${e.num}: quantidade ${e.qty} ultrapassa o saldo disponível para esta caixa (${Math.max(0, avail)} un. restantes).`;
			}
		}
		return null;
	}

	/**
	 * Registra quantidades na caixa ativa e na saída; opcionalmente lacra em seguida.
	 * @param {HTMLElement} node
	 * @param {{ sealAfter?: boolean }} opts
	 */
	async function registerToCurrentBox(node, { sealAfter = false } = {}) {
		const collected = collectEntries(node);
		if (collected.error) {
			notify(collected.error, 'Picking');
			collected.inp?.focus();
			return;
		}

		const { entries = [] } = collected;
		const nonTertiary = entries.filter(e => !e.isTertiary);
		const boxId = getSelectedBoxId();

		if (sealAfter && !boxId) {
			notify('Selecione a caixa aberta que deseja lacrar.', 'Picking');
			return;
		}

		if (nonTertiary.length > 0 && !boxId) {
			notify('Selecione ou abra uma caixa para registrar os itens.', 'Picking');
			node.querySelector('#pick-box-select')?.focus();
			return;
		}

		if (!entries.length && !sealAfter) {
			notify('Informe ao menos uma linha com quantidade maior que zero.', 'Picking');
			return;
		}

		if (nonTertiary.length > 0 && boxId) {
			const capErr = validateBoxCaps(boxId, nonTertiary);
			if (capErr) {
				notify(capErr, 'Picking');
				return;
			}
		}

		const registerBtn = node.querySelector('#pick-box-register-btn');
		const sealBtn = node.querySelector('#pick-box-seal-btn');
		if (registerBtn) registerBtn.disabled = true;
		if (sealBtn) sealBtn.disabled = true;

		try {
			if (nonTertiary.length > 0 && boxId) {
				const box = getSelectedBox();
				const qtyInBox = {};
				for (const bi of (box?.items || [])) {
					qtyInBox[Number(bi.output_item_number)] = Number(bi.quantity) || 0;
				}
				const itemsPayload = nonTertiary.map(e => ({
					output_item_number: e.num,
					quantity: (qtyInBox[e.num] || 0) + e.qty,
					lot_id: items.find(i => Number(i.number) === e.num)?.lot_id ?? null,
					sku: items.find(i => Number(i.number) === e.num)?.product_sku ?? null,
				}));
				const boxRes = await api.call(
					api.set_output_picking_box_items,
					companyId, outputId, boxId,
					{ items: itemsPayload }
				);
				if (boxRes?.error) {
					notify(boxRes.error, 'Erro ao registrar na caixa');
					return;
				}
			}

			if (entries.length > 0) {
				await patchOutputSupplied(entries);
				applySuppliedLocally(entries);
				clearPickInputs(node);
				refreshTable(node);
			}

			if (sealAfter && boxId) {
				if (!await sealCurrentBox(node, boxId)) return;
			} else if (entries.length > 0) {
				const boxCode = getSelectedBox()?.code || '';
				notify(
					`Quantidades registradas na saída${boxCode ? ` e na caixa ${boxCode}` : ''}.`,
					'Picking'
				);
			}

			await reloadBoxes();
			refreshBoxPanel(node);

		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		} finally {
			if (registerBtn) registerBtn.disabled = false;
			if (sealBtn) sealBtn.disabled = false;
		}
	}

	function bindBoxPanelEvents(node) {
		const boxSelect = node.querySelector('#pick-box-select');
		if (boxSelect) {
			if (selectedBoxId) boxSelect.value = selectedBoxId;
			boxSelect.addEventListener('change', () => {
				selectedBoxId = boxSelect.value || null;
			});
		}

		node.querySelector('#pick-box-register-btn')?.addEventListener('click', async () => {
			await registerToCurrentBox(node, { sealAfter: false });
		});

		node.querySelector('#pick-box-seal-btn')?.addEventListener('click', async () => {
			const boxId = getSelectedBoxId();
			if (!boxId) {
				notify('Selecione a caixa aberta que deseja lacrar.', 'Picking');
				return;
			}
			const box = getSelectedBox();
			const collected = collectEntries(node);
			if (collected.error) {
				notify(collected.error, 'Picking');
				return;
			}
			const hasPendingQty = (collected.entries || []).some(e => !e.isTertiary);

			if (hasPendingQty) {
				await registerToCurrentBox(node, { sealAfter: true });
				return;
			}

			const sealed = await sealCurrentBox(node, boxId);
			if (sealed) {
				await reloadBoxes();
				refreshBoxPanel(node);
			}
		});

		node.querySelector('#pick-box-new-btn')?.addEventListener('click', () => {
			openCreateBoxModal(companyId, outputId, async (result) => {
				try {
					await reloadBoxes();
					if (result?.id) {
						selectedBoxId = String(result.id);
					} else {
						const openList = openBoxes();
						if (openList.length > 0) {
							selectedBoxId = String(openList[openList.length - 1].id);
						}
					}
					refreshBoxPanel(node);
					notify('Nova caixa aberta e selecionada como caixa ativa.', 'Picking');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
		});
	}

	const statusLabel = STATUS_LABEL[output.status] || String(output.status);
	const pickerInfo = output.picked_by
		? `<span style="color:#555;font-size:0.8rem">Separador: <strong>${escHtml(output.picked_by)}</strong></span>`
		: '';

	const html = `
		<div class="msgbox" style="min-width:700px;max-width:1000px">
			<div class="mb-title">Picking — Saída #${escHtml(String(outputId))}</div>
			<div class="mb-text" style="max-height:80vh;overflow-y:auto">
				<div style="display:flex;flex-wrap:wrap;gap:1rem;margin-bottom:0.6rem;font-size:0.85rem;color:#444">
					<span>Pedido: <strong>${escHtml(String(output.order_id || '—'))}</strong></span>
					<span>Status: <strong>${escHtml(statusLabel)}</strong></span>
					${output.reference ? `<span>Ref.: <strong>${escHtml(output.reference)}</strong></span>` : ''}
					${pickerInfo}
				</div>
				<p style="margin:0 0 0.4rem;font-size:0.8rem;color:#888">
					Informe a quantidade em <strong>Separar agora</strong>. Os campos <strong>Pendente</strong> atualizam após cada registro.
					${enableBoxCtrl ? ' Use os botões da seção de caixas para registrar, lacrar e abrir nova caixa durante o picking.' : ''}
				</p>
				<div style="overflow-x:auto">
					<table id="pick-items-table" style="width:100%;border-collapse:collapse;font-size:0.86em">
						<thead>
							<tr style="background:#f0f0f0">
								<th style="padding:5px 7px;text-align:left">Linha</th>
								<th style="padding:5px 7px;text-align:left">Posição</th>
								<th style="padding:5px 7px;text-align:left">Produto</th>
								<th style="padding:5px 7px;text-align:left">Lote sep.</th>
								<th style="padding:5px 7px;text-align:right">Req.</th>
								<th style="padding:5px 7px;text-align:right">Já sep.</th>
								<th style="padding:5px 7px;text-align:right">Pendente</th>
								<th style="padding:5px 7px;text-align:right">Separar agora</th>
							</tr>
						</thead>
						<tbody>${buildRowsHtml()}</tbody>
					</table>
				</div>
				${enableBoxCtrl ? buildBoxPanelHtml() : ''}
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y" id="pick-save-btn">Salvar</button>
		</div>`;

	const dlg = dialog.create(html, (key) => {
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);

	const node = dlg.getNode();

	if (enableBoxCtrl) bindBoxPanelEvents(node);

	node.querySelector('#pick-save-btn')?.addEventListener('click', async () => {
		const collected = collectEntries(node);
		if (collected.error) {
			notify(collected.error, 'Picking');
			collected.inp?.focus();
			return;
		}
		const { entries = [] } = collected;

		if (!entries.length) {
			dlg.destroy();
			if (typeof onSuccess === 'function') onSuccess();
			return;
		}

		if (enableBoxCtrl) {
			const nonTertiary = entries.filter(e => !e.isTertiary);
			if (nonTertiary.length > 0) {
				notify(
					'Itens que entram em caixa devem ser registrados com «Registrar nesta caixa» (e lacrar quando necessário), não por este botão.',
					'Picking'
				);
				return;
			}
		}

		const saveBtn = node.querySelector('#pick-save-btn');
		saveBtn.disabled = true;
		saveBtn.textContent = 'Salvando…';

		try {
			await patchOutputSupplied(entries);
			applySuppliedLocally(entries);
			dlg.destroy();
			notify(`Separação da saída #${outputId} registrada com sucesso.`, 'Picking');
			if (typeof onSuccess === 'function') onSuccess();
		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		} finally {
			saveBtn.disabled = false;
			saveBtn.textContent = 'Salvar';
		}
	});
}
